import { useState } from 'react'
import {
  CASStatus,
  alphabeticalCompare,
  CASStatusItem,
} from '@cims-online/shared'
import { useRevertCasMutation } from 'schema'
import { simpleRevertNotification } from 'hooks/notificationHandler'
import { GroupReview } from './components/GroupReview'
import { RouterReview } from './components/RouterReview'
import { useSubmittalTrackerContext } from '../../../../SubmittalTrackerContext'
import { toMoment } from 'utils/toMoment'
import WorkflowRollbackModal from 'components/CIMSModal/WorkflowRollbackModal'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { GroupReviewValues } from './components/GroupReview/types'
import { RouterReviewValues } from './components/RouterReview/types'
import { REReviewValues } from './types'
import { errorHandler } from 'hooks/errorHandler'
import Comments from './components/Comments'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { CommentsValues } from './components/Comments/types'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { dateFormatter } from 'utils/dateFormatter'

export const RevertSubmittalModal: React.VFC = () => {
  const { refetch, revertModal } = useSubmittalTrackerContext()
  const { state, toggle, visible } = revertModal
  const { sharePointFolderName, statusId } = state ?? {}

  const statusIsGreaterThanOrEqualValue = (
    value: CASStatusItem,
    statusId: string
  ) => {
    const status = CASStatus.fromId(statusId)
    if (status === value) return true
    else if (status === CASStatus.GroupReview)
      return CASStatus.REReview === value
    else if (status === CASStatus.REReview) return CASStatus.Closed === value
    else if (status === CASStatus.Closed) return false
    else if (status === CASStatus.Unlocked) return false
    else return true
  }

  const [loading, setLoading] = useState(false)
  const [revert] = useRevertCasMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      revertConstructionAdminSubmittal: {
        submittal: { sharePointFolderName, statusId },
      },
    }) =>
      simpleRevertNotification({
        name: sharePointFolderName,
        status: CASStatus.fromIdOrFail(statusId).name,
      }),
  })

  return (
    <WorkflowRollbackModal
      type="multiple"
      visible={visible}
      loading={loading}
      initialFormValues={
        {
          status: null,
          comment: '',
          contractualReviewDate: toMoment(state?.contractualReviewDate),
          quantity: state?.quantity,
          requestedReviewDate: toMoment(state?.requestedReviewDate),
          routingGroups: toLabeledValues(state?.casToCASRoutingGroups, {
            type: 'callback',
            callback: ({ routingGroup: { id, name, description } }) => ({
              label: name,
              value: id,
              tooltip: description,
            }),
          }),
          type: state?.type
            ? ({
                label: state.type.name,
                value: state.type.id,
                tooltip: state.type.description,
                deleted: state.type.deleted,
              } as LabeledValueType)
            : undefined,
          subtype: state?.subtype
            ? ({
                label: state.subtype.name,
                value: state.subtype.id,
                tooltip: state.subtype.description,
                deleted: state.subtype.deleted,
              } as LabeledValueType)
            : undefined,
          units: state?.units,
          externalLinks: state?.externalLinks.map(({ label, href }) => ({
            label,
            href,
          })),
        } as (
          | GroupReviewValues
          | RouterReviewValues
          | REReviewValues
          | { status: null }
        ) &
          CommentsValues
      }
      onClose={() => toggle(null)}
      onOk={async ({ comment, ...values }) => {
        setLoading(true)
        const common = {
          id: state!.id,
          comment,
          statusId: CASStatus.fromNameOrFail(values.status!).id,
        }
        if (values.status === 'RE Review')
          await revert({
            variables: {
              input: {
                ...common,
                subtypeId: state?.subtype?.id,
              },
            },
          })
        else if (values.status === 'Group Review') {
          const { contractualReviewDate, routingGroups } =
            values as GroupReviewValues
          await revert({
            variables: {
              input: {
                ...common,
                contractualReviewDate: contractualReviewDate.toISOString(),
                routingGroupIds: routingGroups.map(({ value }) =>
                  value.toString()
                ),
                subtypeId: state?.subtype?.id,
              },
            },
          })
        } else if (values.status === 'Router Review') {
          const { quantity, requestedReviewDate, type, units, subtype } =
            values as RouterReviewValues
          await revert({
            variables: {
              input: {
                ...common,
                quantity,
                requestedReviewDate: requestedReviewDate.toISOString(),
                typeId: type.value.toString(),
                subtypeId: subtype?.value?.toString(),
                units,
              },
            },
          })
        } else return errorHandler(`Invalid status: ${values.status}`)
        setLoading(false)
        refetch()
        toggle(null)
      }}
      recordName={sharePointFolderName}
      statuses={[
        {
          statusName: CASStatus.RouterReview.name,
          disabled:
            !statusId ||
            statusIsGreaterThanOrEqualValue(CASStatus.RouterReview, statusId),
          formItems: <RouterReview {...state} />,
        },
        {
          statusName: CASStatus.GroupReview.name,
          disabled:
            !statusId ||
            statusIsGreaterThanOrEqualValue(CASStatus.GroupReview, statusId),
          formItems: <GroupReview />,
        },
        {
          statusName: CASStatus.REReview.name,
          disabled:
            !statusId ||
            statusIsGreaterThanOrEqualValue(CASStatus.REReview, statusId),
        },
      ]}
      formItems={<Comments />}
      parentPositions={{ formItems: 'after' }}
      affectedItems={(statusName) => [
        {
          label: 'SharePoint Packet',
          value: state?.packetSharePointResource?.name,
          hidden: !state?.packetSharePointResource?.id,
        },
        { label: 'Disposition', value: state?.dispositionType?.name },
        {
          label: 'Disposition Date',
          value: dateFormatter.simple(state?.dispositionDate),
        },
        { label: 'Disposition By', value: state?.dispositionBy?.mail },
        {
          label: 'Contractual Review Date',
          value: dateFormatter.simple(state?.contractualReviewDate),
          hidden: statusName === CASStatus.REReview.name,
        },
        {
          label: 'Routing Groups',
          value: state?.casToCASRoutingGroups
            .sort((a, b) =>
              alphabeticalCompare(a.routingGroup, b.routingGroup, 'name')
            )
            .map(({ routingGroup, accepted, acceptedBy, acceptedDate }) => ({
              label: routingGroup.name,
              deletable: true,
              value: [
                {
                  label: 'Accepted',
                  value: !!accepted ? 'True' : 'False',
                },
                {
                  label: 'Accepted By',
                  value: acceptedBy?.mail,
                },
                {
                  label: 'Accepted Date',
                  value: dateFormatter.simple(acceptedDate),
                },
              ],
            })),
          hidden: statusName === CASStatus.REReview.name,
        },
        {
          label: 'Requested Review Date',
          value: dateFormatter.simple(state?.requestedReviewDate),
          hidden: statusName !== CASStatus.RouterReview.name,
        },
        {
          label: 'Type',
          value: state?.type?.name,
          hidden: statusName !== CASStatus.RouterReview.name,
        },
        {
          label: 'Subtype',
          value: state?.subtype?.name,
          hidden: statusName !== CASStatus.RouterReview.name,
        },
        {
          label: 'Quantity',
          value: state?.quantity.toString(),
          hidden: statusName !== CASStatus.RouterReview.name,
        },
        {
          label: 'Units',
          value: state?.units,
          hidden: statusName !== CASStatus.RouterReview.name,
        },
      ]}
    />
  )
}
